// apps/web/src/features/finance/revenue/RevenueDashboardPage.tsx (#602)
//
// Admin-only revenue dashboard. The route is wrapped by `AdminGate` so
// this component does NOT re-check the permission; it only renders the
// `useRevenueDashboard` result and surfaces typed error codes through
// the existing `ApiClientError` → toast-style i18n mapping.
//
// What we render
// --------------
//  - Month <Select> sourced from `availableMonths` (descending) — the
//    selector is a controlled component; `selectedMonth.key` is the
//    initial value and any change re-fires the query with `?month=`.
//  - KPI total + 3 category cards (buyouts / foreignTrade / logistics).
//  - Two comparison cards (previousMonth, previousYear). The
//    discriminated union is narrowed on `available` so the renderer
//    branches once, then trusts the typed payload — no defensive
//    `?? 0` on the numeric fields.
//  - SVG cumulative chart (no chart library). Baseline + 3 Y-axis
//    ticks; no hover/tooltip by design.
//  - Manager table — sheet order preserved; duplicate names kept as
//    separate rows (T3/T4 backend defaults).
//  - Source link to the Google Sheets workbook with `target=_blank
//    rel=noopener noreferrer` and an inline warning about the
//    "anyone-with-link" exposure of the upstream workbook.
//
// States
// ------
//  loading / error+retry / not-configured (503 REVENUE_NOT_CONFIGURED)
//  / source-invalid (503 REVENUE_SOURCE_INVALID) / source-unavailable
//  (502 REVENUE_SOURCE_UNAVAILABLE) / invalid-month (400) /
//  month-not-found (404) / stale (200 with freshness.isStale=true).
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode } from '@magimp/shared';
import type {
  RevenueCumulativePoint,
  RevenueDashboard,
  RevenueManagerRow,
  RevenueMonthKey,
} from '@magimp/shared';
import { ApiClientError } from '../../../api/errors.js';
import { Button, ErrorState, LoadingState, Select } from '../../../ui/index.js';
import { useLocale } from '../../../i18n/useLocale.js';
import {
  formatDateTime,
  formatNumber,
  formatRub,
  formatRubCompact,
} from '../../../i18n/formatters.js';
import { useRevenueDashboard } from './queries.js';
import { ComparisonCard } from './RevenueComparisonCard.js';

const PAGE_GAP = 16;
const CARD_GAP = 12;
const CHART_HEIGHT = 220;
const CHART_PADDING_X = 32;
const CHART_PADDING_Y = 16;

export function RevenueDashboardPage() {
  const { t } = useTranslation(['revenue', 'common']);
  const [monthKey, setMonthKey] = useState<RevenueMonthKey | undefined>(undefined);
  const query = useRevenueDashboard(monthKey);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: PAGE_GAP,
        minHeight: 0,
        flex: 1,
        padding: 8,
      }}
    >
      <header style={{ display: 'flex', gap: CARD_GAP, alignItems: 'center', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: '1 1 auto' }}>
          {t('revenue:title')}
        </h1>
      </header>
      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <RevenueErrorView error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <RevenueDashboardContent
          data={query.data}
          selectedKey={monthKey ?? query.data.selectedMonth.key}
          onMonthChange={setMonthKey}
          onRefresh={() => void query.refetch()}
          isRefreshing={query.isFetching}
          t={t}
        />
      )}
    </div>
  );
}

type T = ReturnType<typeof useTranslation<['revenue', 'common']>>['t'];

interface RevenueDashboardContentProps {
  data: RevenueDashboard;
  selectedKey: RevenueMonthKey;
  onMonthChange: (key: RevenueMonthKey) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  t: T;
}

function RevenueDashboardContent({
  data,
  selectedKey,
  onMonthChange,
  onRefresh,
  isRefreshing,
  t,
}: RevenueDashboardContentProps) {
  const { locale } = useLocale();
  const monthOptions = useMemo(
    () => data.availableMonths.map((m) => ({ value: m.key, label: m.label })),
    [data.availableMonths],
  );

  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor="revenue-month" style={{ fontSize: 13, color: 'var(--fg-2)' }}>
          {t('revenue:monthSelectLabel')}
        </label>
        <Select
          value={selectedKey}
          onChange={(v) => onMonthChange(v as RevenueMonthKey)}
          options={monthOptions}
        />
        <Button kind="secondary" icon="rotate-ccw" onClick={onRefresh} disabled={isRefreshing}>
          {t('common:actions.reload')}
        </Button>
      </div>

      {data.freshness.isStale ? <StaleBanner data={data} t={t} /> : null}

      <FreshnessBadge data={data} locale={locale} t={t} />

      <KpiBlock data={data} locale={locale} t={t} />

      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: CARD_GAP,
        }}
      >
        <ComparisonCard
          title={t('revenue:comparison.previousMonth')}
          comp={data.comparisons.previousMonth}
          locale={locale}
          t={t}
        />
        <ComparisonCard
          title={t('revenue:comparison.previousYear')}
          comp={data.comparisons.previousYear}
          locale={locale}
          t={t}
        />
      </section>

      <ChartBlock points={data.cumulativeDaily} locale={locale} t={t} />

      <ManagerTable rows={data.managers} locale={locale} t={t} />

      <SourceLink data={data} t={t} />
    </>
  );
}

// -- KPI -------------------------------------------------------------------

interface KpiBlockProps {
  data: RevenueDashboard;
  locale: ReturnType<typeof useLocale>['locale'];
  t: T;
}

function KpiBlock({ data, locale, t }: KpiBlockProps) {
  const cards: { id: 'total' | 'buyouts' | 'foreignTrade' | 'logistics'; value: number }[] = [
    { id: 'total', value: data.totals.total },
    { id: 'buyouts', value: data.categories.buyouts },
    { id: 'foreignTrade', value: data.categories.foreignTrade },
    { id: 'logistics', value: data.categories.logistics },
  ];
  return (
    <section
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: CARD_GAP,
      }}
    >
      {cards.map((c) => (
        <div
          key={c.id}
          style={{
            background: 'var(--bg-1)',
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--r-2)',
            padding: 12,
          }}
        >
          <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>{t(`revenue:kpi.${c.id}`)}</div>
          <div
            style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}
            title={formatRub(c.value, locale)}
          >
            {formatRubCompact(c.value, locale)}
          </div>
        </div>
      ))}
    </section>
  );
}

// -- Chart -----------------------------------------------------------------

interface ChartBlockProps {
  points: readonly RevenueCumulativePoint[];
  locale: ReturnType<typeof useLocale>['locale'];
  t: T;
}

function ChartBlock({ points, locale, t }: ChartBlockProps) {
  if (points.length === 0) {
    return (
      <section
        style={{
          border: '1px solid var(--border-1)',
          borderRadius: 'var(--r-2)',
          padding: 16,
          background: 'var(--bg-1)',
          color: 'var(--fg-2)',
          fontSize: 13,
        }}
      >
        {t('revenue:chart.empty')}
      </section>
    );
  }
  const max = Math.max(...points.map((p) => p.total));
  const innerHeight = CHART_HEIGHT - CHART_PADDING_Y * 2;
  // WHY: nominal viewBox width — the SVG scales to its container via
  // `width="100%"` while keeping the line shape stable at any size.
  const innerWidth = 600 - CHART_PADDING_X * 2;
  const stepX = points.length > 1 ? innerWidth / (points.length - 1) : 0;
  const path = points
    .map((p, idx) => {
      const x = CHART_PADDING_X + stepX * idx;
      const y =
        CHART_PADDING_Y + (max > 0 ? innerHeight - (p.total / max) * innerHeight : innerHeight);
      return `${idx === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const ticks = [0, 0.5, 1].map((r) => max * r);

  return (
    <section
      style={{
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        padding: 12,
        background: 'var(--bg-1)',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        {t('revenue:chart.title')}
      </div>
      <svg
        viewBox={`0 0 600 ${CHART_HEIGHT}`}
        width="100%"
        height={CHART_HEIGHT}
        role="img"
        aria-label={t('revenue:chart.title')}
      >
        {ticks.map((tick, i) => {
          const y = CHART_PADDING_Y + (innerHeight - (max > 0 ? (tick / max) * innerHeight : 0));
          return (
            <g key={i}>
              <line
                x1={CHART_PADDING_X}
                x2={600 - CHART_PADDING_X}
                y1={y}
                y2={y}
                stroke="var(--border-1)"
                strokeDasharray={i === 0 ? '0' : '3 3'}
              />
              <text x={4} y={y + 4} fontSize={10} fill="var(--fg-3)">
                {formatNumber(Math.round(tick), locale)}
              </text>
            </g>
          );
        })}
        <path d={path} fill="none" stroke="var(--accent-1)" strokeWidth={2} />
      </svg>
    </section>
  );
}

// -- Manager table ---------------------------------------------------------

interface ManagerTableProps {
  rows: readonly RevenueManagerRow[];
  locale: ReturnType<typeof useLocale>['locale'];
  t: T;
}

function ManagerTable({ rows, locale, t }: ManagerTableProps) {
  if (rows.length === 0) {
    return null;
  }
  return (
    <section
      style={{
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-1)',
        overflowX: 'auto',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--fg-2)' }}>
            <th style={{ padding: 8 }}>{t('revenue:manager.name')}</th>
            <th style={{ padding: 8, textAlign: 'right' }}>{t('revenue:manager.total')}</th>
            <th style={{ padding: 8, textAlign: 'right' }}>{t('revenue:manager.buyouts')}</th>
            <th style={{ padding: 8, textAlign: 'right' }}>{t('revenue:manager.foreignTrade')}</th>
            <th style={{ padding: 8, textAlign: 'right' }}>{t('revenue:manager.logistics')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={`${row.name}-${idx}`} style={{ borderTop: '1px solid var(--border-1)' }}>
              <td style={{ padding: 8 }}>{row.name}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{formatRub(row.total, locale)}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{formatRub(row.buyouts, locale)}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>
                {formatRub(row.foreignTrade, locale)}
              </td>
              <td style={{ padding: 8, textAlign: 'right' }}>{formatRub(row.logistics, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// -- Source link + stale --------------------------------------------------

function SourceLink({ data, t }: { data: RevenueDashboard; t: T }) {
  return (
    <footer
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 12,
        color: 'var(--fg-2)',
      }}
    >
      <div>
        {t('revenue:source.label')}{' '}
        <a
          href={data.source.url}
          target="_blank"
          rel="noopener noreferrer"
          title={t('revenue:source.warning')}
          style={{ color: 'var(--accent-1)' }}
        >
          {data.source.label}
        </a>
      </div>
      <div style={{ color: 'var(--fg-3)' }}>{t('revenue:source.warning')}</div>
    </footer>
  );
}

/**
 * #602 line 39: "compact freshness status, e.g. last successful refresh
 * time and stale marker". Renders only the time part — the stale marker
 * lives in `<StaleBanner>` and only shows when the response is stale.
 */
function FreshnessBadge({
  data,
  locale,
  t,
}: {
  data: RevenueDashboard;
  locale: ReturnType<typeof useLocale>['locale'];
  t: T;
}) {
  return (
    <div
      style={{
        fontSize: 12,
        color: 'var(--fg-2)',
      }}
    >
      {t('revenue:freshness.fetchedAt', { time: formatDateTime(data.freshness.fetchedAt, locale) })}
    </div>
  );
}

function StaleBanner({ data, t }: { data: RevenueDashboard; t: T }) {
  const reasonKey = data.freshness.staleReason
    ? `revenue:stale.${data.freshness.staleReason}`
    : 'revenue:stale.generic';
  return (
    <div
      role="status"
      style={{
        background: 'var(--status-warn-bg)',
        color: 'var(--status-warn-fg)',
        border: '1px solid var(--status-warn-fg)',
        borderRadius: 'var(--r-2)',
        padding: 10,
        fontSize: 13,
      }}
    >
      {t('revenue:stale.banner')} — {t(reasonKey as 'revenue:stale.generic')}
    </div>
  );
}

// -- Error view ------------------------------------------------------------

function RevenueErrorView({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation(['revenue', 'common']);
  if (error instanceof ApiClientError) {
    if (error.code === ErrorCode.REVENUE_NOT_CONFIGURED) {
      return (
        <ErrorState
          title={t('revenue:errors.notConfiguredTitle')}
          text={t('revenue:errors.notConfiguredText')}
        />
      );
    }
    if (error.code === ErrorCode.REVENUE_SOURCE_INVALID) {
      return (
        <ErrorState
          title={t('revenue:errors.sourceInvalidTitle')}
          text={t('revenue:errors.sourceInvalidText')}
        />
      );
    }
    if (error.code === ErrorCode.REVENUE_MONTH_NOT_FOUND) {
      return (
        <ErrorState
          title={t('revenue:errors.monthNotFoundTitle')}
          text={t('revenue:errors.monthNotFoundText')}
          onRetry={onRetry}
        />
      );
    }
    if (error.code === ErrorCode.REVENUE_INVALID_MONTH) {
      return (
        <ErrorState
          title={t('revenue:errors.invalidMonthTitle')}
          text={t('revenue:errors.invalidMonthText')}
        />
      );
    }
    if (error.code === ErrorCode.REVENUE_SOURCE_UNAVAILABLE) {
      return (
        <ErrorState
          title={t('revenue:errors.sourceUnavailableTitle')}
          text={t('revenue:errors.sourceUnavailableText')}
          onRetry={onRetry}
        />
      );
    }
  }
  return <ErrorState onRetry={onRetry} />;
}
